import { memo } from "react";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import { EntityAvatar } from "@components/avatar/EntityAvatar";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { AgentRunActivityDrawer } from "@components/presence/AgentRunActivityDrawer";
import { AgentWorkingActivity } from "@components/presence/AgentWorkingActivity";
import type { BoardCardField } from "@design-system/Board/Board.types";
import { BoardCard } from "@design-system/Board/BoardCard";
import { BoardTruncatedText } from "@design-system/Board/BoardTruncatedText";
import { BoardCardLayout } from "@design-system/Board/BoardCardLayout";
import { BoardCardMenu } from "@design-system/Board/BoardCardMenu";
import { Menu } from "@design-system/Menu/Menu.lib";
import { useUserStatus } from "@hooks/useUserStatus";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    type ClientsWorkOverviewCallbacks,
    type WorkRowConfirmationRequest,
    WorkRowActionItems,
} from "../WorkRowActions";
import { useWorkAgentRun, useWorkCardPresence } from "./useWorkCardPresence";
import {
    WorkCardDeliveryDateControl,
    WorkCardPresence,
} from "./WorkCardControls";
import {
    type WorkCardFieldColumn,
    withHealthForProgress,
} from "./workCardFieldColumns";
import { getWorkCardTagFields } from "./workCardFields";

export interface WorkBoardCardProps {
    row: ClientsWorkOverviewWorkRow;
    /** The tags, in order: a Kick view's own, else List's visible columns. */
    fieldColumns: readonly WorkCardFieldColumn[];
    /** Off when the columns already name the client. */
    isClientShown: boolean;
    /** The card whose details are open beside the board. */
    isActive: boolean;
    /** Opens the card's details beside the board. */
    onOpen: (row: ClientsWorkOverviewWorkRow) => void;
    callbacks: ClientsWorkOverviewCallbacks | undefined;
    /** Opens the board's one Cancel or Delete confirmation. */
    onConfirmationRequest: (request: WorkRowConfirmationRequest) => void;
    /** The board's hidden text naming the actions shortcut. */
    actionsDescriptionId: string;
}

/** The card's title: the entity, or the service when the entity has no name. */
export const getWorkCardTitle = (row: ClientsWorkOverviewWorkRow) =>
    row.entityName || row.name;

/**
 * The card's context line: `Client · Service`, without the client when the
 * columns name it and without the service when the title already does.
 */
export const getWorkCardContextText = ({
    row,
    isClientShown,
}: Readonly<Pick<WorkBoardCardProps, "row" | "isClientShown">>) =>
    [
        ...(isClientShown ? [row.workspaceName] : []),
        ...(row.entityName ? [row.name] : []),
    ].join(" · ");

/** The card's context line as its stacked field; empty when there's no text. */
export const getWorkCardContextField = (
    args: Readonly<Pick<WorkBoardCardProps, "row" | "isClientShown">>,
): BoardCardField => {
    const contextText = getWorkCardContextText(args);
    return {
        id: "context",
        placement: "stacked",
        content: contextText !== "" && (
            <BoardTruncatedText>{contextText}</BoardTruncatedText>
        ),
    };
};

/** The card's accessible name: its title, then its context line. */
export const getWorkCardLabel = (
    args: Readonly<Pick<WorkBoardCardProps, "row" | "isClientShown">>,
) =>
    [getWorkCardTitle(args.row), getWorkCardContextText(args)]
        .filter((part) => part !== "")
        .join(", ");

/** The entity's avatar before the card's title, which names it. */
export const WorkCardEntityAvatar = ({
    row,
}: Readonly<Pick<WorkBoardCardProps, "row">>) => (
    <EntityAvatar
        size="xs"
        entity={{
            name: row.entityName,
            iconColor: row.entityIconColor,
            logo: row.entityLogo,
        }}
        aria-hidden
    />
);

/**
 * One application on the Work board: the entity, a `Client · Service`
 * context line, read-only tags (a Kick view's own, else List's visible
 * columns, stage first), and who is on it (the agent, viewers, the
 * engagement team) bottom right. Click or Enter opens its details beside
 * the board; the hover kebab, right-click and Shift+F10 open the same
 * actions.
 * Once the agent has run on its close, the run's status and history peek out
 * underneath (`AgentRunActivityDrawer`); while the organization's run list
 * first loads, a running agent shows the "Agent working" row from the row.
 */
export const WorkBoardCard = memo(
    ({
        row,
        fieldColumns,
        isClientShown,
        isActive,
        onOpen,
        callbacks,
        onConfirmationRequest,
        actionsDescriptionId,
    }: WorkBoardCardProps) => {
        const { data: userStatus } = useUserStatus();
        const { run, isAgentWorking } = useWorkAgentRun(row);
        const presenceMembers = useWorkCardPresence({ row, isAgentWorking });
        const renderDrawer = () => {
            if (run) {
                return <AgentRunActivityDrawer run={run} />;
            }
            return isAgentWorking ? <AgentWorkingActivity /> : undefined;
        };
        const isDemo = isBusyClosesDemoId(row.closeId);
        const open = () => onOpen(row);
        const actionItems = (
            <WorkRowActionItems
                row={row}
                callbacks={callbacks}
                onConfirmationRequest={onConfirmationRequest}
            />
        );
        const fields: BoardCardField[] = [
            getWorkCardContextField({ row, isClientShown }),
            ...getWorkCardTagFields({
                row,
                fieldColumns: withHealthForProgress({
                    fieldColumns,
                    health: row.health,
                }),
                userId: userStatus?.user.id,
            }).map((field) =>
                field.id === ClientsWorkOverviewColumn.WORK_DELIVERY_DATE &&
                field.content
                    ? {
                          ...field,
                          content: <WorkCardDeliveryDateControl row={row} />,
                      }
                    : field,
            ),
            {
                id: "presence",
                placement: "bottomEnd",
                content: presenceMembers.length > 0 && (
                    <WorkCardPresence row={row} members={presenceMembers} />
                ),
            },
        ];

        const card = (
            <BoardCard
                isActive={isActive}
                onClick={isDemo ? undefined : open}
                aria-label={getWorkCardLabel({ row, isClientShown })}
                aria-describedby={isDemo ? undefined : actionsDescriptionId}
                drawer={renderDrawer()}
            >
                <BoardCardLayout
                    leading={<WorkCardEntityAvatar row={row} />}
                    title={getWorkCardTitle(row)}
                    menu={
                        isDemo ? undefined : (
                            <BoardCardMenu aria-label="Work actions">
                                {actionItems}
                            </BoardCardMenu>
                        )
                    }
                    fields={fields}
                />
            </BoardCard>
        );

        if (isDemo) {
            return card;
        }
        return <Menu.Context trigger={card}>{actionItems}</Menu.Context>;
    },
);

WorkBoardCard.displayName = "WorkBoardCard";
